import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, Inject, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY_ARRAY_LENGTH, NotificationType } from '@modeso/aumico-lib-core-fe';
import { GetUserDto, IUserGeneralInformation, updateCurrentUser } from '@modeso/aumico-lib-users-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import * as countries from 'i18n-iso-countries';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject, of } from 'rxjs';
import { Constants } from '../../../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { Paths } from '../../../enums/paths.enum';
import { CountriesMapper } from '../../../shared/mappers/countries.mapper';
import { CountriesService } from '../../../shared/services/countries.service';
import { BasePageComponent } from '../../base.page';

@UntilDestroy()
@Component({
    selector: 'app-page-edit-user-general-information',
    templateUrl: './edit-user-general-information.page.html',
    styleUrls: ['./edit-user-general-information.page.scss'],
})
export class EditUserGeneralInformationPage extends BasePageComponent implements OnInit {
    protected countries: INameId[];
    private readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    private initialUserGeneralInformationData: IUserGeneralInformation | undefined;
    private modifiedUserGeneralInformation: IUserGeneralInformation | undefined;

    private isWarnPopupOpened = false;

    readonly proceedButtonEvent$ = new Subject<void>();
    saveUser = new Subject<void>();
    userCountry$: Observable<string>;
    constructor(
        injector: Injector,
        dialog: MatDialog,
        private readonly countriesMapper: CountriesMapper,
        private readonly countriesService: CountriesService,
        @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    ) {
        super(injector, dialog);
        this.disableProceedButton = false;
        this.prepareCountries();
        this.onSaveSuccessfully = this.onSaveSuccessfully.bind(this);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.actionsListener$
            .pipe(ofType(updateCurrentUser.success), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(updateCurrentUser.failure), untilDestroyed(this))
            .subscribe((response) => this.onSaveFailed(response.payload));

        this.userCountry$ = this.countriesService.getUserCountry$();
    }

    private prepareCountries(): void {
        const retrievedCountriesByUSerLanguage: countries.LocalizedCountryNames<{ select: 'official' }> =
            this.countriesService.getCountriesByLanguage(this.currentLanguage);
        this.countries = this.countriesMapper.mapCountries(retrievedCountriesByUSerLanguage);
    }

    onInitialUserGeneralInformation(userGeneralInformationChanges: IUserGeneralInformation): void {
        this.initialUserGeneralInformationData = userGeneralInformationChanges;
    }

    onUserGeneralInformationChange(userGeneralInformationChanges: IUserGeneralInformation): void {
        this.modifiedUserGeneralInformation = userGeneralInformationChanges;
    }

    saveButtonClicked = (): void => {
        this.proceedButtonEvent$.next();
    };

    protected override isDataChanged(): Observable<boolean> {
        const isDataChangesSaved = fastDeepEqual(
            this.initialUserGeneralInformationData,
            this.modifiedUserGeneralInformation,
        );
        this.isWarnPopupOpened = !isDataChangesSaved;
        return of(!isDataChangesSaved);
    }

    private onSaveSuccessfully(): void {
        const isLanguageChanged =
            this.initialUserGeneralInformationData.language !== this.modifiedUserGeneralInformation.language;
        this.initialUserGeneralInformationData = cloneDeep<IUserGeneralInformation>(
            this.modifiedUserGeneralInformation,
        );
        const savedUser: Partial<GetUserDto> = this.mapUserInformationToGetUserDto();
        this.authProvider.setCurrentUser(savedUser);
        if (isLanguageChanged) {
            this.applySelectedLanguage();
        }
        this.savedSuccessfully.next(true);
    }

    private onSaveFailed(errorResponse: HttpErrorResponse): void {
        let errorDescriptionLocalizationKey: string = Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;

        if (errorResponse.status === HttpStatusCode.BadRequest) {
            if (
                (this.modifiedUserGeneralInformation.name != null &&
                    this.modifiedUserGeneralInformation.name.length === EMPTY_ARRAY_LENGTH) ||
                (this.modifiedUserGeneralInformation.surname != null &&
                    this.modifiedUserGeneralInformation.surname.length === EMPTY_ARRAY_LENGTH)
            ) {
                errorDescriptionLocalizationKey = Constants.ERROR_DIALOG.FIRST_AND_LAST_NAMES_REQUIRED;
            } else {
                errorDescriptionLocalizationKey = Constants.ERROR_DIALOG.PHONE_NUMBER_INVALID;
            }
        }

        this.aumicoCoreProvider.dispatchAddNotification(
            'aumico_reports_notification_error_title',
            errorDescriptionLocalizationKey,
            NotificationType.ERROR,
        );
        this.savedSuccessfully.next(false);
    }

    private applySelectedLanguage(): void {
        const selectedLanguage = this.modifiedUserGeneralInformation.language;

        if (selectedLanguage == null || selectedLanguage === '') {
            this.updateLocalization('de-ch');
            this.i18NextService.changeLanguage('de-ch').then((x) => {
                if (!this.isWarnPopupOpened) {
                    this.router.navigateByUrl(
                        `de-ch/${Paths.HOME}/${Paths.USER_ACCOUNT}/${Paths.USER_GENERAL_INFORMATION}`,
                    );
                }

                document.location.reload();
            });
        }

        if (selectedLanguage != null && selectedLanguage !== '') {
            this.updateLocalization(selectedLanguage);
            this.i18NextService.changeLanguage(selectedLanguage).then((x) => {
                if (!this.isWarnPopupOpened) {
                    this.router.navigateByUrl(
                        `${selectedLanguage}/${Paths.HOME}/${Paths.USER_ACCOUNT}/${Paths.USER_GENERAL_INFORMATION}`,
                    );
                }
            });
        }

        setTimeout(() => {
            document.location.reload();
        }, 1000);
    }
    private updateLocalization(selectedLanguage: string) {
        console.log('user settings, change *** language to: ', selectedLanguage);
        this.localStorageService.setCurrentLanguage(selectedLanguage);
    }
    private mapUserInformationToGetUserDto(): Partial<GetUserDto> {
        return {
            countryISO31661Alpha2Code: this.modifiedUserGeneralInformation.country?.id,
            firstName: this.modifiedUserGeneralInformation.name,
            lastName: this.modifiedUserGeneralInformation.surname,
            locale: this.modifiedUserGeneralInformation.language,
            title: this.modifiedUserGeneralInformation.salutation,
            phoneNumber: this.modifiedUserGeneralInformation.phone,
        };
    }
    override save(): Observable<void> {
        this.saveUser.next();
        return of(void 0);
    }
}
